 
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useState } from 'react'
import {
    Segment,
    Header,
    Icon,
    Label,
    Table,
    Button,
    Dropdown,
    Popup,
} from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getDisputes } from '../../services/Disputes/disputes.service'
import { subDays, format } from 'date-fns'
import useUser from '../Shared/UserProvider/useUser'
import formatDate from '../../helpers/formatDate'
import { useDeviceMediaQuery } from '../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import DisputesSummaryHeader from './DisputesSummaryHeader'

const statusColor = {
    OPEN: 'grey',
    CONTESTED: 'orange',
    RECOVERED: 'green',
    PARTIALLY_RECOVERED: 'green',
    REFUNDED: 'red',
}

const DisputesList = () => {
    const [t] = useTranslation()
    const { user } = useUser()
    const { isMobile } = useDeviceMediaQuery()

    const statusOptions = [
        { key: '', text: t('dispute.filter.all'), value: '' },
        { key: 'OPEN', text: t('dispute.status_values.OPEN'), value: 'OPEN' },
        { key: 'CONTESTED', text: t('dispute.status_values.CONTESTED'), value: 'CONTESTED' },
        { key: 'RECOVERED', text: t('dispute.status_values.RECOVERED'), value: 'RECOVERED' },
        { key: 'PARTIALLY_RECOVERED', text: t('dispute.status_values.PARTIALLY_RECOVERED'), value: 'PARTIALLY_RECOVERED' },
        { key: 'REFUNDED', text: t('dispute.status_values.REFUNDED'), value: 'REFUNDED' },
    ]

    const periodOptions = [
        { key: 7, text: t('dispute.filter.last_7_days'), value: 7 },
        { key: 30, text: t('dispute.filter.last_30_days'), value: 30 },
        { key: 90, text: t('dispute.filter.last_90_days'), value: 90 },
    ]

    const [statusFilter, setStatusFilter] = useState('')
    const [periodDays, setPeriodDays] = useState(30)

    const endDate = format(new Date(), 'yyyy-MM-dd')
    const startDate = format(subDays(new Date(), periodDays), 'yyyy-MM-dd')

    const { data: response, isLoading } = useQuery({
        queryKey: ['disputes', startDate, endDate, statusFilter],
        queryFn: () => getDisputes({ sd: startDate, ed: endDate, status: statusFilter || undefined }),
    })

    const disputes = response || []

    const totalDisputeAmount = disputes.reduce((sum, d) => sum + (d.disputeAmount || 0), 0)
    const totalRecoveredAmount = disputes.reduce((sum, d) => sum + (d.recoveredAmount || 0), 0)
    const totalUnrecoveredAmount = totalDisputeAmount - totalRecoveredAmount
    const recoveryRate = totalDisputeAmount > 0 ? Math.round((totalRecoveredAmount / totalDisputeAmount) * 100) : 0

    const disputeItems = disputes.flatMap((d) => d.disputeItems || [])
    const autoRecoverableItemsCount = disputeItems.filter((item) => {
        const recoveringProofElements = item.recoveringProofElements
        return recoveringProofElements && (
            recoveringProofElements.pickingMethod === 'SCAN' ||
            recoveringProofElements.pickingProofs === 'MAYBE'
        )
    }).length
    const autoRecoverableRate = disputeItems.length > 0
        ? Math.round((autoRecoverableItemsCount / disputeItems.length) * 100)
        : 0

    const formatMoney = (cents) => {
        if (cents === null || cents === undefined) return '—'
        return (cents / 100).toLocaleString(user.getCompanyLocale(), {
            style: 'currency',
            currency: user.getCompanyCurrency(),
        })
    }

    return (
        <div>
            <Segment
              css={css`
                  &.ui.segment {
                      margin: 0;
                  }
              `}
            >
                                <DisputesSummaryHeader
                                    isMobile={isMobile}
                                    autoRecoverableRate={autoRecoverableRate}
                                    totalRecoveredAmount={totalRecoveredAmount}
                                    totalDisputeAmount={totalDisputeAmount}
                                    totalUnrecoveredAmount={totalUnrecoveredAmount}
                                    recoveryRate={recoveryRate}
                                    formatMoney={formatMoney}
                                    t={t}
                                />
            </Segment>

            <Segment>
                <div css={css`display: flex; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap;`}>
                    <Dropdown
                        selection
                        options={periodOptions}
                        value={periodDays}
                        onChange={(_, { value }) => setPeriodDays(value)}
                    />
                    <Dropdown
                        selection
                        placeholder={t('dispute.filter.status')}
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(_, { value }) => setStatusFilter(value)}
                        clearable
                    />
                </div>

                {isLoading ? (
                    <Segment loading basic />
                ) : disputes.length === 0 ? (
                    <Segment placeholder>
                        <Header icon>
                            <Icon name="check circle outline" />
                            {t('dispute.no_disputes')}
                        </Header>
                    </Segment>
                ) : (
                    <Table celled selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t('dispute.order_id')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.platform')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.date')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.status')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.dispute_amount')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.recovered_amount')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.customer_feedback')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.has_picking_proofs')}</Table.HeaderCell>
                                <Table.HeaderCell></Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {disputes.map((dispute) => {
                                const platformName = dispute.posBrandPlatform?.platform?.name || dispute.platform || '—'
                                const brandName = dispute.posBrandPlatform?.brand?.name || '—'
                                const hasPickingProofs = dispute.disputeItems?.some(item =>
                                    item.recoveringProofElements &&
                                    (item.recoveringProofElements.pickingMethod === 'SCAN' ||
                                     item.recoveringProofElements.pickingProofs === 'MAYBE')
                                )
                                const customerFeedback = dispute.customerDetails?.inputDescription || dispute.disputeDetails?.input_description || '—'

                                return (
                                    <Table.Row key={dispute.orderId}>
                                        <Table.Cell><Link to={`/orders/${dispute.orderId}`}>{dispute.orderId}</Link></Table.Cell>
                                        <Table.Cell>
                                            {platformName}{platformName && brandName ? ' - ' : ''}{brandName}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {dispute.disputeCreationDate
                                                ? formatDate(dispute.disputeCreationDate)
                                                : '—'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Label color={statusColor[dispute.status] || 'grey'}>
                                                {dispute.status ? t(`dispute.status_values.${dispute.status}`) : '—'}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell>{formatMoney(dispute.disputeAmount)}</Table.Cell>
                                        <Table.Cell>{formatMoney(dispute.recoveredAmount)}</Table.Cell>
                                        <Table.Cell>
                                            {customerFeedback !== '—' ? (
                                                <Popup
                                                    trigger={
                                                        <span css={css`max-width: 200px; display: inline-block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer;`}>
                                                            {customerFeedback}
                                                        </span>
                                                    }
                                                    content={customerFeedback}
                                                    hoverable
                                                />
                                            ) : '—'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {hasPickingProofs ? (
                                                <Icon name="check circle" color="green" title={t('dispute.has_picking_proofs')} />
                                            ) : (
                                                <Icon name="times circle" color="red" />
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Button
                                                as={Link}
                                                to={`/order/${dispute.orderId}/dispute`}
                                                size="tiny"
                                                color="teal"
                                            >
                                                {t('global.see')}
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            })}
                        </Table.Body>
                    </Table>
                )}
            </Segment>
        </div>
    )
}

export default DisputesList
